/** Sample-code generators + "Try it" runner for the API Reference.
 *
 *  Every operation gets runnable curl / Python / JavaScript snippets built
 *  from the live OpenAPI operation (parameters, examples, request body
 *  schema). GET operations can additionally be executed in-browser with
 *  the current admin session (or a pasted bearer token). */
import React, { useMemo, useState } from "react";
import axios from "axios";
import { Copy, Play, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { inlineSchema } from "./ApiReferenceSchema";
import { getBackendUrl as resolveBackendUrl } from "@/lib/runtimeConfig";

/** Generate a plausible example value for a resolved schema property.
 *  Mirrors the Pydantic Field defaults / examples / type so customers
 *  get a copy-paste-ready body even when the developer didn't write a
 *  hand-crafted example. Recurses up to depth 3 to keep payloads small. */
export function sampleValue(prop, spec, depth = 0) {
    if (!prop || depth > 3) return null;
    if (prop.$ref) return sampleValue(inlineSchema(prop, spec), spec, depth + 1);
    if (prop.examples && prop.examples.length) return prop.examples[0];
    if (prop.example !== undefined) return prop.example;
    if (prop.default !== undefined && prop.default !== null) return prop.default;
    if (prop.enum && prop.enum.length) return prop.enum[0];
    if (prop.anyOf || prop.oneOf) {
        const arr = (prop.anyOf || prop.oneOf).filter((s) => s.type !== "null");
        return arr.length ? sampleValue(arr[0], spec, depth + 1) : null;
    }
    if (prop.allOf && prop.allOf.length) return sampleValue(prop.allOf[0], spec, depth + 1);
    if (prop.type === "string") {
        if (prop.format === "email") return "user@example.com";
        if (prop.format === "date-time") return new Date().toISOString();
        if (prop.format === "uuid") return "00000000-0000-0000-0000-000000000000";
        return "string";
    }
    if (prop.type === "integer") return Math.max(1, prop.minimum ?? 1);
    if (prop.type === "number") return prop.minimum ?? 0;
    if (prop.type === "boolean") return false;
    if (prop.type === "array") return [sampleValue(prop.items || {}, spec, depth + 1)];
    if (prop.type === "object" || prop.properties) {
        const out = {};
        const props = prop.properties || {};
        for (const [k, v] of Object.entries(props)) out[k] = sampleValue(v, spec, depth + 1);
        return out;
    }
    return null;
}

function paramExample(p, spec) {
    const ex = (p.examples && p.examples[0]) ?? p.example ?? p.schema?.examples?.[0] ?? p.schema?.example ?? p.schema?.default;
    if (ex !== undefined && ex !== null && ex !== "") return Array.isArray(ex) ? ex[0] : ex;
    return null;
}

function tokenPlaceholder(op) {
    const scheme = (op.op.security?.[0] && Object.keys(op.op.security[0])[0]) || null;
    return scheme === "ApiKeyAuth" ? "tf_live_…"
        : scheme === "BearerJWT" ? "eyJhbGciOi…  # admin JWT"
        : scheme === "BearerJWT_Superdomain" ? "eyJhbGciOi…  # superdomain JWT"
        : null;
}

/** Normalised request pieces shared by all three generators. */
export function requestParts(op, spec) {
    const method = (op.method || "GET").toUpperCase();
    const params = op.op.parameters || [];
    let path = op.path || "/";
    for (const p of params.filter((x) => x.in === "path")) {
        const ex = paramExample(p, spec);
        path = path.replace(`{${p.name}}`, ex !== null ? encodeURIComponent(String(ex)) : `<${p.name}>`);
    }
    const query = [];
    for (const p of params.filter((x) => x.in === "query")) {
        const ex = paramExample(p, spec);
        if (p.required || ex !== null) {
            query.push([p.name, ex !== null ? ex : `<${p.name}>`]);
        }
        if (query.length >= 6) break;
    }
    const content = op.op.requestBody?.content || {};
    let body = null;
    if (["POST", "PUT", "PATCH", "DELETE"].includes(method)) {
        if (content["application/json"]) {
            body = { kind: "json", value: sampleValue(inlineSchema(content["application/json"].schema, spec), spec) ?? {} };
        } else if (content["multipart/form-data"]) {
            const schema = inlineSchema(content["multipart/form-data"].schema, spec) || {};
            const files = [];
            const fields = [];
            for (const [name, prop] of Object.entries(schema.properties || {})) {
                if (prop.type === "string" && prop.format === "binary") files.push([name, "/path/to/file.pdf"]);
                else if (prop.type === "array" && prop.items?.format === "binary") { files.push([name, "/path/to/primary.pdf"]); files.push([name, "/path/to/amendment.pdf"]); }
                else fields.push([name, (prop.examples && prop.examples[0]) ?? prop.example ?? sampleValue(prop, spec) ?? ""]);
            }
            body = { kind: "multipart", files, fields };
        } else if (content["application/octet-stream"]) {
            body = { kind: "binary" };
        }
    }
    return { method, path, query, body, token: tokenPlaceholder(op) };
}

function qs(query) {
    return query.length ? "?" + query.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`).join("&") : "";
}

export function buildCurl(op, spec) {
    const { method, path, query, body, token } = requestParts(op, spec);
    const host = resolveBackendUrl() || "https://api.example.com";
    const lines = [`curl -X ${method} "${host}${path}${qs(query)}"`];
    if (token) lines.push(`  -H "Authorization: Bearer ${token}"`);
    if (body?.kind === "json") {
        lines.push(`  -H "Content-Type: application/json"`);
        const json = JSON.stringify(body.value, null, 2).split("\n").map((l, i) => (i === 0 ? l : "  " + l)).join("\n");
        lines.push(`  -d '${json}'`);
    } else if (body?.kind === "multipart") {
        for (const [n, f] of body.files) lines.push(`  -F "${n}=@${f}"`);
        for (const [n, v] of body.fields) lines.push(`  -F "${n}=${v}"`);
    } else if (body?.kind === "binary") {
        lines.push(`  -H "Content-Type: application/octet-stream"`);
        lines.push(`  --data-binary @/path/to/file.pdf`);
    }
    return lines.join(" \\\n");
}

function pyLiteral(v, indent = 4) {
    return JSON.stringify(v, null, indent).replace(/\btrue\b/g, "True").replace(/\bfalse\b/g, "False").replace(/\bnull\b/g, "None");
}

export function buildPython(op, spec) {
    const { method, path, query, body, token } = requestParts(op, spec);
    const host = resolveBackendUrl() || "https://api.example.com";
    const lines = ["import httpx", "", `API = "${host}"`];
    if (token) lines.push(`TOKEN = "${token.split("  #")[0]}"${token.includes("#") ? "  #" + token.split("#")[1] : ""}`);
    lines.push("");
    const args = [`f"{API}${path}"`];
    if (query.length) args.push(`params=${pyLiteral(Object.fromEntries(query), 4).replace(/\n/g, "\n    ")}`);
    if (token) args.push('headers={"Authorization": f"Bearer {TOKEN}"}');
    if (body?.kind === "json") args.push(`json=${pyLiteral(body.value, 4).replace(/\n/g, "\n    ")}`);
    else if (body?.kind === "multipart") {
        if (body.files.length) args.push(`files=[${body.files.map(([n, f]) => `("${n}", open("${f}", "rb"))`).join(", ")}]`);
        if (body.fields.length) args.push(`data=${pyLiteral(Object.fromEntries(body.fields), 4).replace(/\n/g, "\n    ")}`);
    } else if (body?.kind === "binary") {
        args.push('content=open("/path/to/file.pdf", "rb")');
        args.push('headers={"Authorization": f"Bearer {TOKEN}", "Content-Type": "application/octet-stream"}');
    }
    args.push("timeout=60");
    lines.push(`r = httpx.${method.toLowerCase()}(`);
    for (const a of args) lines.push(`    ${a},`);
    lines.push(")");
    lines.push("r.raise_for_status()");
    lines.push("print(r.status_code, r.json())");
    return lines.join("\n");
}

export function buildJs(op, spec) {
    const { method, path, query, body, token } = requestParts(op, spec);
    const host = resolveBackendUrl() || "https://api.example.com";
    const lines = [`const API = "${host}";`];
    if (token) lines.push(`const TOKEN = "${token.split("  #")[0]}";${token.includes("#") ? " //" + token.split("#")[1] : ""}`);
    lines.push("");
    const headers = [];
    if (token) headers.push("Authorization: `Bearer ${TOKEN}`");
    let bodyExpr = null;
    if (body?.kind === "json") { headers.push('"Content-Type": "application/json"'); bodyExpr = `JSON.stringify(${JSON.stringify(body.value, null, 2).replace(/\n/g, "\n  ")})`; }
    else if (body?.kind === "multipart") {
        lines.push("const form = new FormData();");
        for (const [n, f] of body.files) lines.push(`form.append("${n}", fileInput.files[0]); // ${f}`);
        for (const [n, v] of body.fields) lines.push(`form.append("${n}", ${JSON.stringify(String(v))});`);
        bodyExpr = "form";
    } else if (body?.kind === "binary") { headers.push('"Content-Type": "application/octet-stream"'); bodyExpr = "fileInput.files[0]"; }
    lines.push(`const res = await fetch(\`\${API}${path}${qs(query)}\`, {`);
    lines.push(`  method: "${method}",`);
    if (headers.length) lines.push(`  headers: { ${headers.join(", ")} },`);
    if (bodyExpr) lines.push(`  body: ${bodyExpr},`);
    lines.push("});");
    lines.push("if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);");
    lines.push("console.log(await res.json());");
    return lines.join("\n");
}

const TABS = [
    { id: "curl", label: "curl", build: buildCurl, tone: "text-amber-200" },
    { id: "python", label: "Python", build: buildPython, tone: "text-sky-200" },
    { id: "js", label: "JavaScript", build: buildJs, tone: "text-violet-200" },
];

export function SampleCodeTabs({ op, spec }) {
    const [tab, setTab] = useState("curl");
    const key = `${op.method}-${op.path}`;
    const snippets = useMemo(() => Object.fromEntries(TABS.map((t) => [t.id, t.build(op, spec)])), [op, spec]);
    const active = TABS.find((t) => t.id === tab) || TABS[0];
    const copy = () => {
        try { navigator.clipboard.writeText(snippets[active.id]); toast.success(`${active.label} sample copied`); } catch { /* noop */ }
    };
    return (
        <div data-testid={`sample-code-${key}`}>
            <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                    <span className="text-white/40 uppercase tracking-wider text-[10px]">Sample code</span>
                    <div className="flex items-center gap-1">
                        {TABS.map((t) => (
                            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`sample-tab-${t.id}-${key}`}
                                className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${tab === t.id ? "border-white/40 bg-white/10 text-white" : "border-white/10 text-white/50 hover:border-white/25"}`}>
                                {t.label}
                            </button>
                        ))}
                    </div>
                </div>
                <button onClick={copy} className="text-[10px] text-white/40 hover:text-white inline-flex items-center gap-1" data-testid={`copy-curl-${key}`}>
                    <Copy className="w-3 h-3" /> copy
                </button>
            </div>
            <pre className={`text-[10px] bg-black/40 border border-white/10 rounded px-3 py-2 overflow-auto font-mono leading-relaxed ${active.tone}`}
                data-testid={`sample-snippet-${active.id}-${key}`}>{snippets[active.id]}</pre>
        </div>
    );
}

/** In-browser runner. GET only — write operations must be exercised from
 *  a terminal / test suite against a non-production tenant. */
export function TryItPanel({ op, spec }) {
    const key = `${op.method}-${op.path}`;
    const params = op.op.parameters || [];
    const pathParams = params.filter((p) => p.in === "path");
    const queryParams = params.filter((p) => p.in === "query");
    const [values, setValues] = useState(() => {
        const init = {};
        for (const p of params) {
            const ex = paramExample(p, spec);
            init[`${p.in}:${p.name}`] = ex !== null ? String(ex) : "";
        }
        return init;
    });
    const [token, setToken] = useState("");
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const isGet = op.method === "GET";

    const run = async () => {
        let path = op.path;
        for (const p of pathParams) {
            const v = values[`path:${p.name}`];
            if (!v) { toast.error(`Path parameter "${p.name}" is required`); return; }
            path = path.replace(`{${p.name}}`, encodeURIComponent(v));
        }
        const q = {};
        for (const p of queryParams) {
            const v = values[`query:${p.name}`];
            if (v !== "" && v !== undefined) q[p.name] = v;
        }
        const bearer = token.trim() || localStorage.getItem("tf_token") || "";
        setBusy(true);
        const t0 = performance.now();
        try {
            const r = await axios.get(`${resolveBackendUrl()}${path}`, {
                params: q, headers: bearer ? { Authorization: `Bearer ${bearer}` } : {},
                validateStatus: () => true, timeout: 60000, transformResponse: [(d) => d],
            });
            let text = typeof r.data === "string" ? r.data : JSON.stringify(r.data);
            try { text = JSON.stringify(JSON.parse(text), null, 2); } catch { /* keep raw */ }
            setResult({ status: r.status, ms: Math.round(performance.now() - t0), text: text.length > 20000 ? text.slice(0, 20000) + "\n… (truncated)" : text });
        } catch (e) {
            setResult({ status: 0, ms: Math.round(performance.now() - t0), text: String(e?.message || e) });
        } finally { setBusy(false); }
    };

    return (
        <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3" data-testid={`try-it-${key}`}>
            <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
                <span className="text-white/40 uppercase tracking-wider text-[10px]">Try it</span>
                {isGet ? (
                    <button onClick={run} disabled={busy} data-testid={`try-it-run-${key}`}
                        className="inline-flex items-center gap-1.5 text-[11px] px-3 py-1 rounded-full border border-emerald-400/40 bg-emerald-400/10 text-emerald-200 hover:bg-emerald-400/20 disabled:opacity-50">
                        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />} Send request
                    </button>
                ) : (
                    <span className="text-[10px] text-white/40" data-testid={`try-it-readonly-${key}`}>
                        Live runs are enabled for GET only — copy a sample above and run it against a non-production tenant.
                    </span>
                )}
            </div>
            {isGet && (
                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                    {[...pathParams, ...queryParams].map((p) => (
                        <label key={`${p.in}:${p.name}`} className="text-[10px] text-white/50 flex flex-col gap-0.5">
                            <span className="font-mono">{p.name}{p.required && <span className="text-rose-400"> *</span>} <span className="text-white/30">({p.in})</span></span>
                            <input value={values[`${p.in}:${p.name}`] ?? ""} onChange={(e) => setValues((v) => ({ ...v, [`${p.in}:${p.name}`]: e.target.value }))}
                                placeholder={p.schema?.type || ""} data-testid={`try-it-param-${p.name}-${key}`}
                                className="bg-black/40 border border-white/10 rounded px-2 py-1 text-[11px] text-white/85 font-mono" />
                        </label>
                    ))}
                    <label className="text-[10px] text-white/50 flex flex-col gap-0.5">
                        <span>Bearer token override <span className="text-white/30">(blank = your admin session)</span></span>
                        <input type="password" value={token} onChange={(e) => setToken(e.target.value)} placeholder="tf_live_… or JWT"
                            data-testid={`try-it-token-${key}`}
                            className="bg-black/40 border border-white/10 rounded px-2 py-1 text-[11px] text-white/85 font-mono" />
                    </label>
                </div>
            )}
            {result && (
                <div className="mt-2" data-testid={`try-it-result-${key}`}>
                    <div className="flex items-center gap-2 mb-1 text-[10px]">
                        <code className={`px-1.5 py-0.5 rounded ${result.status >= 200 && result.status < 300 ? "bg-emerald-400/15 text-emerald-300" : result.status >= 400 ? "bg-amber-400/15 text-amber-300" : "bg-rose-400/15 text-rose-300"}`}
                            data-testid={`try-it-status-${key}`}>{result.status || "ERR"}</code>
                        <span className="text-white/40">{result.ms} ms</span>
                    </div>
                    <pre className="text-[10px] bg-black/50 border border-white/10 rounded px-3 py-2 overflow-auto max-h-80 font-mono leading-relaxed text-white/80">{result.text}</pre>
                </div>
            )}
        </div>
    );
}
